import {
  AdBreakType,
  AdInsertionType,
  AdMarker,
  AdResumePayload,
  AdServerOptions,
  AdTrackingEvent,
  AdVideoVariant,
  API_ERROR,
  AWPError,
  BufferSeekingPayload,
  convertVodIAdBreaksToAdMarkers,
  CoreEvents,
  CoreEventsMap,
  ERROR_CATEGORY,
  ErrorLevel,
  EventEmitter,
  getDCIDBasedOnService,
  getRemoteConfigValue,
  getTopLocationHost,
  IAd,
  IAdBreak,
  isMtvOrUutisetService,
  storage,
  TimePayload,
  UNKNOWN_AD,
} from "@tv4/avod-web-player-common";
import * as Yospace from "@yospace/admanagement-sdk";
import { Session, SessionDVRLive, SessionVOD } from "@yospace/admanagement-sdk";
import { XmlNode } from "@yospace/admanagement-sdk/types/Core/XmlNode";

import { MediaID3Payload } from "../media_engine/utils/mediaConstants";
import {
  TSSAIAdEngineParams,
  TSSAISeekHandler,
} from "../playback/SSAIPlayback";
import { isBumpersAllowed } from "../util";
import { hasAdImmunity, setAdImmunity } from "../utils/adImmunity";
import { SSAIPlaybackPolicy } from "./util/SSAIPlaybackPolicy";

type TGetSessionUrl = {
  baseUrl: string;
  gdprConsent?: string;
  trackingData: AdServerOptions;
  sessionId: string;
  bumpers: boolean;
  startOver?: boolean;
  sessionType?: SessionType;
  adUuid: string;
};

export function getSessionUrl({
  baseUrl,
  gdprConsent,
  trackingData,
  bumpers,
  startOver = false,
  sessionType,
  adUuid,
  sessionId,
}: TGetSessionUrl) {
  const url = new URL(baseUrl);
  const tags = trackingData.tags?.split(",") ?? [];
  const shares = trackingData.shares;

  // useFreeWheelTestEnv exists for adtech environment testing https://github.com/TV4/freewheel-testing-browser-extension
  const project =
    storage.getSessionData("useFreeWheelTestEnv") === true
      ? "freewheeltest"
      : "freewheel";
  url.searchParams.set("project", project);
  url.searchParams.set("wire_session_id", sessionId);

  // dur value for parameter yo.lpa (Live Positional Analytics) is used for DVR Live playback ONLY; default value is "false"
  if (sessionType === SessionType.DVRLIVE)
    url.searchParams.set("yo.lpa", "dur");

  if (storage.getSessionData("debugYospace") === true) {
    // this is for temporarily add session logging on server level
    url.searchParams.set("yo.ad", "true");
  }

  const service = getRemoteConfigValue("SERVICE");
  tags.push(getTopLocationHost());

  if (!bumpers) {
    tags.push("NOBUMPERS");
  }
  if (startOver) {
    tags.push("startover");
  }
  if (trackingData.contentForm && isMtvOrUutisetService(service)) {
    tags.push(trackingData.contentForm);
  }

  url.searchParams.append("t", tags.join(","));
  url.searchParams.append("s", shares);
  url.searchParams.append("dcid", getDCIDBasedOnService(service));
  url.searchParams.append("pid", adUuid);
  url.searchParams.set("st", "0:0,20:1");

  if (gdprConsent) {
    if (isMtvOrUutisetService(service)) {
      url.searchParams.set("cp.gdpr", "1");
      url.searchParams.set("cp.gdpr_consent", gdprConsent);
      url.searchParams.set("cp.gdpr_pd", "0");
    } else {
      url.searchParams.set("gdpr", "1");
      url.searchParams.set("gdpr_consent", gdprConsent);
    }
  }

  if (isMtvOrUutisetService(service)) {
    url.searchParams.set("pf", "html5");
    url.searchParams.set("ci", trackingData.contentId);
  }

  return url.toString();
}

export enum SessionType {
  VOD,
  DVRLIVE,
}

function extractVariantFromYospaceAdNode(
  node?: XmlNode
): AdVideoVariant | undefined {
  if (!node) {
    return undefined;
  }

  const children = node.getChildren();

  for (const child of children) {
    if (child.getName() === "CreativeParameter") {
      // the customAdType can be any casing, and need to be lowercased and converted to AdVideoVariant
      const customAdType = child.getInnerText().toLowerCase();

      const adVariant = {
        bumper: AdVideoVariant.VIGNETTE,
        trailer: AdVideoVariant.TRAILER,
        sponsor: AdVideoVariant.SPONSOR,
      }[customAdType];

      if (adVariant) {
        return adVariant;
      }
    }

    const childAdVariant = extractVariantFromYospaceAdNode(child);

    if (childAdVariant) {
      return childAdVariant;
    }
  }

  return undefined;
}

function parseAdvert({
  advert,
  isLive,
}: {
  advert?: Yospace.Advert;
  isLive: boolean;
}): IAd {
  if (!advert) {
    return UNKNOWN_AD;
  }

  const creative = advert.getLinearCreative();

  const universalAdId: string | undefined =
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- documentation does not match
    //@ts-ignore need to cheat a bit because Yospace's type casting is incorrect
    advert.getLinearCreative().getUniversalAdIds()?.[0]?.value ??
    advert.getLinearCreative().getUniversalAdIds()?.[0];

  const adTitleProperty = advert
    .getProperties()
    .find((property) => property.getName() === "AdTitle");

  const ad: IAd = {
    id: advert.getIdentifier(),
    universalAdId,
    variant:
      extractVariantFromYospaceAdNode(advert.getExtensions() || undefined) ||
      AdVideoVariant.NORMAL,
    title: adTitleProperty?.getValue() ?? "",
    creative: {
      id: creative.getCreativeIdentifier(),
      adId: creative.getAdvertIdentifier(),
      type: "?",
      duration: Math.round(creative.getDuration() / 1000),
      mediafiles: [creative.getAssetUri()],
      trackingEvents: {},
      ...(!isLive && {
        clickThroughUrlTemplate: {
          id: creative.getCreativeIdentifier(),
          url: creative.getClickThroughUrl(),
        },
      }),
    },
  };

  const clickThrough = creative.getClickTrackingUrls();
  if (clickThrough && clickThrough[0]) {
    ad.creative.trackingEvents = {
      [AdTrackingEvent.CLICK_THROUGH]: [clickThrough[0]],
    };
  }

  Array.from(creative.getTrackingMap(), ([, value]) => {
    const newEvents = {
      ...ad.creative.trackingEvents,
      [value.trackingEvent]: value.trackingUrls,
    };
    ad.creative.trackingEvents = newEvents;
  });
  return ad;
}

export class SSAIAdEngine extends EventEmitter<CoreEventsMap> {
  public readonly insertionType = AdInsertionType.ServerSide;
  private sessionType: SessionType;
  private sessionUrl: string;

  private session?: Yospace.Session;

  private started: boolean = false;
  private paused: boolean = false;
  private redirecting: boolean = false;
  private seekPosition: number | null = null;

  private seekHandler: TSSAISeekHandler;
  private watchedAdBreakStartTimes = new Set<number>();
  private currentAd?: IAd;
  private adImmunityDuration = getRemoteConfigValue("AD_IMMUNITY_DURATION");
  private nextAdBreakPosition = 0;
  private assetId: string;
  private adsRequired = true;
  private destroyed = false;

  private lastStreamPosition?: number;

  constructor(options: TSSAIAdEngineParams) {
    super();
    this.seekHandler = options.seekHandler;
    this.assetId = options.metadata.asset.id;
    this.adsRequired = !options.capabilities.skip_ads;
    this.sessionType = options.media.isLive
      ? SessionType.DVRLIVE
      : SessionType.VOD;

    this.sessionUrl = getSessionUrl({
      baseUrl: options.media.accessUrl || options.media.manifestUrl,
      gdprConsent: options.gdprConsent,
      trackingData: options.metadata.adBreak.trackingData,
      sessionId: options.sessionId,
      bumpers: isBumpersAllowed(options.metadata),
      sessionType: this.sessionType,
      startOver: options.media.isStartOver,
      adUuid: options.adUuid,
    });
  }

  private setSession(session: Yospace.Session) {
    this.session = session;

    session.setPlaybackPolicyHandler(new SSAIPlaybackPolicy());
    session.addAnalyticObserver({
      onAdvertBreakStart: this.onAdvertBreakStart.bind(this),
      onAdvertBreakEnd: this.onAdvertBreakEnd.bind(this),
      onAdvertEnd: this.onAdvertEnd.bind(this),
      onAdvertStart: this.onAdvertStart.bind(this),
      onTrackingEvent: () => {
        //
      },
      onAnalyticUpdate: () => {
        //
      },
      onAdvertBreakEarlyReturn: () => {
        // TODO: Adding handlers for these events is now required by the SDK, and we should investigate if
        // it's appropriate to handle these events in the future.
      },
      onSessionError: () => {
        // TODO: Adding handlers for these events is now required by the SDK, and we should investigate if
        // it's appropriate to handle these events in the future.
      },
      onTrackingError: () => {
        // TODO: Adding handlers for these events is now required by the SDK, and we should investigate if
        // it's appropriate to handle these events in the future.
      },
    });
  }

  private parseAdvertBreak(adBreak: Yospace.AdBreak): IAdBreak {
    return {
      ads: adBreak
        .getAdverts()
        .map((advert: Yospace.Advert) =>
          parseAdvert({ advert, isLive: this.sessionType !== SessionType.VOD })
        ),
      timeOffset: Math.trunc(
        this.getContentPositionForPlayheadPosition(adBreak.getStart() / 1000)
      ),
      breakType:
        adBreak.getStart() === 0 && this.sessionType === SessionType.VOD
          ? AdBreakType.Preroll
          : AdBreakType.Midroll,
      insertionType: AdInsertionType.ServerSide,
    };
  }

  private getAdBreaks(): IAdBreak[] {
    const adBreaks =
      this.session?.getAdBreaksByType(
        Yospace.AdBreak.getBreakTypeFromString("LINEAR")
      ) || [];
    return adBreaks.map((adBreak) => this.parseAdvertBreak(adBreak));
  }

  public getVodAdMarkers(): AdMarker[] {
    return convertVodIAdBreaksToAdMarkers(this.getAdBreaks());
  }

  public markAdBreaksBeforeStartTimeAsWatched(startTime: number): void {
    if (this.sessionType !== SessionType.VOD) {
      return;
    }
    const adBreaks =
      this.session?.getAdBreaksByType(
        Yospace.AdBreak.getBreakTypeFromString("LINEAR")
      ) || [];
    const startTimeMillis = startTime * 1000;
    adBreaks.forEach((adBreak: Yospace.AdBreak) => {
      const adBreakStart = adBreak.getStart();
      if (adBreakStart <= startTimeMillis) {
        adBreak.setInactive();
        this.watchedAdBreakStartTimes.add(adBreakStart);
      }
    });
  }

  public hasAdImmunity(): boolean {
    if (!this.adsRequired) return true;
    return hasAdImmunity(this.assetId, this.adImmunityDuration);
  }

  private onAdvertBreakStart(adBreak?: Yospace.AdBreak) {
    if (!adBreak) return;

    this.emit(CoreEvents.BREAK_START, {
      adBreak: this.parseAdvertBreak(adBreak),
    });
  }
  private onAdvertBreakEnd(_evt: unknown) {
    const endedAdBreakStartTime = this.session?.getCurrentAdBreak()?.getStart();
    if (endedAdBreakStartTime !== undefined) {
      this.watchedAdBreakStartTimes.add(endedAdBreakStartTime);
    }
    if (this.seekPosition !== null) {
      this.redirectPlayerPosition(this.seekPosition);
      this.seekPosition = null;
    }
    setAdImmunity(this.assetId);
    this.emit(CoreEvents.BREAK_END, {});
  }

  private onAdvertStart(advert: Yospace.Advert) {
    const ad = parseAdvert({
      advert,
      isLive: this.sessionType !== SessionType.VOD,
    });
    this.currentAd = ad;
    this.emit(CoreEvents.AD_LOADING, {
      ad,
    });
    this.emit(CoreEvents.AD_MEDIA_LOADING, {
      src: advert.getLinearCreative()?.getAssetUri() || "",
    });
    this.emit(CoreEvents.AD_LOADED, {
      ad,
    });
    if (this.started) {
      this.emit(CoreEvents.AD_START, {
        ad,
      });
    }
  }

  private onAdvertEnd() {
    this.emit(CoreEvents.AD_ENDED, undefined);
  }

  private getAdBreakForPosition(position: number) {
    const adBreaks =
      this.session?.getAdBreaksByType(
        Yospace.AdBreak.getBreakTypeFromString("LINEAR")
      ) ?? [];
    return adBreaks.find((adBreak) => {
      const start = adBreak.getStart() / 1000;
      const end = (adBreak.getStart() + adBreak.getDuration()) / 1000;
      return position >= start && position < end;
    });
  }

  // This is done according to instructions from Yospace, in order to have the logs show up in the browser console
  // despite Webpack extracting comments (which we normally want to avoid in production builds)
  private enableGlobalDebugLogging() {
    const globalLogger = /*#__PURE__*/ function () {
      function Logger() {} // eslint-disable-line @typescript-eslint/no-empty-function -- Necessary to override the logger according to Yospace's instructions
      Logger.debug = function debug(string: string) {
        global.console.log(string);
      };
      Logger.error = function error(string: string) {
        global.console.error(string);
      };
      Logger.info = function info(string: string) {
        global.console.info(string);
      };
      Logger.warn = function warn(string: string) {
        global.console.warn(string);
      };
      return Logger;
    };

    Yospace.YoLog.setDebugFlags(Yospace.DebugFlags.DEBUG_ALL);
    Yospace.YoLog.setLogger(globalLogger());
  }

  private createYospaceSession(): Promise<SessionVOD | SessionDVRLive> {
    // Set a debug flag for debugging or validation purposes.
    // If validating, also disable all other logging so that as clean logs as possible can be submitted to Yospace for validation.
    // YoLog.setDebugFlags(DebugFlags.DEBUG_VALIDATION);

    if (storage.getSessionData("debugYospace") === true) {
      this.enableGlobalDebugLogging();
    }

    const properties = new Yospace.SessionProperties();
    properties.setUserAgent(navigator.userAgent);
    properties.setRequestTimeout(15000);

    if (this.sessionType === SessionType.VOD) {
      return Yospace.SessionVOD.create(this.sessionUrl, properties);
    }
    return Yospace.SessionDVRLive.create(this.sessionUrl, properties);
  }

  public async createSession(): Promise<string | null> {
    let session: Session;

    try {
      session = await this.createYospaceSession();
    } catch (e) {
      // The yospace sdk sometimes throws the failed session
      // instead of returning it.
      if (e instanceof Session) {
        session = e;
      } else {
        throw new AWPError({
          context: "core",
          message: `[SSAIAdEngine] session initialization threw`,
          category: ERROR_CATEGORY.API,
          raw: e,
          code: API_ERROR.YOSPACE,
          errorLevel: ErrorLevel.PLAYER,
        });
      }
    }

    if (this.destroyed) return null;

    if (session.getSessionState() !== Yospace.SessionState.INITIALISED) {
      throw new AWPError({
        context: "core",
        message: `[SSAIAdEngine] could not initialize session, code: ${session.getResultCode()}`,
        category: ERROR_CATEGORY.API,
        code: API_ERROR.YOSPACE,
        errorLevel: ErrorLevel.PLAYER,
      });
    }
    this.setSession(session);
    return session.getPlaybackUrl();
  }

  public isInAdBreak() {
    return !!this.session?.getCurrentAdBreak();
  }

  public getContentPositionForPlayheadPosition(playheadPosition: number) {
    if (this.session instanceof Yospace.SessionVOD) {
      const playheadPositionMillis = playheadPosition * 1000;
      const contentPositionMillis =
        this?.session.getContentPositionForPlayhead(playheadPositionMillis) ??
        0;
      return contentPositionMillis / 1000;
    }
    return playheadPosition;
  }

  public getPlayheadPositionForContentPosition(contentPosition: number) {
    if (this.session instanceof Yospace.SessionVOD) {
      const contentPositionMillis = contentPosition * 1000;
      const playheadPositionMillis =
        this?.session.getPlayheadForContentPosition(contentPositionMillis) ?? 0;
      return playheadPositionMillis / 1000;
    }
    return contentPosition;
  }

  private getAdTimePayload(
    contentTimePayload: TimePayload,
    advert: Yospace.Advert
  ): TimePayload {
    const remainingTime = advert.getRemainingTime(
      contentTimePayload.currentTime * 1000
    );
    const currentTime = (advert.getDuration() - remainingTime) / 1000;
    return {
      ...contentTimePayload,
      currentTime,
      duration: advert.getDuration() / 1000,
      isInAdBreak: true,
    };
  }

  private redirectPlayerPosition(position: number) {
    this.redirecting = true;
    this.seekHandler(position);
  }

  public handlePlaying(payload: TimePayload) {
    const { currentTime } = payload;
    this.session?.onPlayerEvent(
      this.started ? Yospace.PlayerEvent.RESUME : Yospace.PlayerEvent.START,
      currentTime * 1000
    );

    if (!this.started && this.currentAd) {
      this.emit(CoreEvents.AD_START, {
        ad: this.currentAd,
      });
    }

    this.session?.onPlayheadUpdate(currentTime * 1000);
    this.started = true;
    const advert = this.session?.getCurrentAdvert();

    if (advert) {
      if (this.paused) {
        const resumePayload: AdResumePayload = {
          ...this.getAdTimePayload(payload, advert),
          ad: this.currentAd,
        };
        this.emit(CoreEvents.AD_RESUME, resumePayload);
        this.paused = false;
      }
      return true;
    }
    this.paused = false;
    return false;
  }

  public handlePaused(payload: TimePayload) {
    const { currentTime } = payload;
    this.paused = true;
    this.session?.onPlayerEvent(Yospace.PlayerEvent.PAUSE, currentTime * 1000);
    const advert = this.session?.getCurrentAdvert();
    if (advert) {
      this.emit(CoreEvents.AD_PAUSED, this.getAdTimePayload(payload, advert));
      return true;
    }
    return false;
  }

  public handleTimeUpdate(payload: TimePayload) {
    const { currentTime } = payload;
    // Before updating the session we check if we're about to play an adbreak,
    // and if ad immunity is active or the adbreak has already been played we skip it and avoid informing the
    // session about it so that it doesn't trigger a BREAK_START event.
    // We only do this if we're not in an ad break as that may result in a redirect loop.
    const upcomingBreak =
      this.lastStreamPosition !== undefined &&
      !this.getAdBreakForPosition(this.lastStreamPosition)
        ? this.getAdBreakForPosition(currentTime)
        : undefined;
    const adBreaks = this.getAdBreaks();
    const contentTime = this.getContentPositionForPlayheadPosition(currentTime);

    this.lastStreamPosition = payload.currentTime;

    this.nextAdBreakPosition =
      adBreaks.find((adBreak) => adBreak.timeOffset >= contentTime)
        ?.timeOffset || Infinity;

    const isUpcomingBreakWatched =
      upcomingBreak?.isActive() === false &&
      this.watchedAdBreakStartTimes.has(upcomingBreak.getStart());

    const upcomingBreakEnd = upcomingBreak
      ? (upcomingBreak.getStart() + upcomingBreak.getDuration()) / 1000
      : undefined;

    if (
      this.sessionType === SessionType.VOD &&
      upcomingBreakEnd !== undefined &&
      (isUpcomingBreakWatched || this.hasAdImmunity())
    ) {
      this.redirectPlayerPosition(upcomingBreakEnd);
      return true;
    }

    this.session?.onPlayheadUpdate(currentTime * 1000);
    // if the onPlayheadUpdate lead to a break starting the time_update should be considered an AD_TIME_UPDATE
    const currentAdBreak = this.session?.getCurrentAdBreak();
    const advert = this.session?.getCurrentAdvert();
    if (advert && currentAdBreak?.isActive()) {
      this.emit(
        CoreEvents.AD_TIME_UPDATED,
        this.getAdTimePayload(payload, advert)
      );
      // Block the content time update for VOD only
      return this.sessionType === SessionType.VOD;
    }
    return false;
  }

  private handleSeek(payload: BufferSeekingPayload) {
    const contentTime = this.getContentPositionForPlayheadPosition(
      payload.currentTime
    );

    if (this.hasAdImmunity() || contentTime < this.nextAdBreakPosition)
      return false;

    if (this.session) {
      const seekToMillis = payload.currentTime * 1000;

      // see SSAIPlaybackPolicy.ts for the logic for willSeekTo.`
      const redirectPosition = this.session?.willSeekTo(seekToMillis) / 1000;

      // make sure we don't redirect if it's a gap in the stream
      if (Math.abs(redirectPosition - payload.currentTime) > 0.5) {
        this.seekPosition = payload.currentTime;
        this.redirectPlayerPosition(redirectPosition);

        // The eventflow is seeking -> seeked -> break_start since this seeked is ignored we need
        // to fake a successful seeked event.
        this.emit(CoreEvents.SEEKED, {
          ...payload,
          currentTime: contentTime,
          duration: this.getContentPositionForPlayheadPosition(
            payload.duration
          ),
        });
        return true;
      }
    }
    return false;
  }

  public handleSeeking(_payload: BufferSeekingPayload) {
    if (this.redirecting) {
      return true;
    }
    if (this.isInAdBreak()) {
      // handleSeeked will restore the position thus eliminating this
      // seeking event so we make sure to ignore it.
      return true;
    }
    return false;
  }

  public handleSeeked(payload: BufferSeekingPayload) {
    if (this.redirecting) {
      this.redirecting = false;
      return true;
    }

    const handled = this.handleSeek(payload);
    if (handled) {
      return handled;
    }

    this.session?.onPlayerEvent(
      Yospace.PlayerEvent.SEEK,
      payload.currentTime * 1000
    );
    if (this.isInAdBreak()) {
      // TODO: how to handle this case?
      return true;
    }
    return false;
  }

  public handleBuffering(payload: TimePayload) {
    const { currentTime } = payload;
    this.session?.onPlayerEvent(Yospace.PlayerEvent.STALL, currentTime * 1000);
    const advert = this.session?.getCurrentAdvert();

    if (advert) {
      this.emit(CoreEvents.AD_BUFFERING, {
        ...this.getAdTimePayload(payload, advert),
        playing: !this.paused,
      });
      return true;
    }
    return false;
  }

  public handleBuffered(payload: TimePayload) {
    const { currentTime } = payload;
    this.session?.onPlayerEvent(
      Yospace.PlayerEvent.CONTINUE,
      currentTime * 1000
    );
    const advert = this.session?.getCurrentAdvert();

    if (advert) {
      this.emit(CoreEvents.AD_BUFFERED, {
        ...this.getAdTimePayload(payload, advert),
        playing: !this.paused,
      });
      return true;
    }
    return false;
  }

  public handleEnded({ currentTime }: TimePayload) {
    const wasInBreak = this.isInAdBreak();
    this.session?.onPlayerEvent(Yospace.PlayerEvent.STOP, currentTime * 1000);

    this.started = false;

    if (wasInBreak) {
      // the event emit happens by the analyticsObserver
      return true;
    }
    return false;
  }

  public handleID3({ startTime, id3 }: MediaID3Payload) {
    if (id3 && id3.YMID && id3.YSEQ && id3.YTYP && id3.YDUR) {
      const meta = Yospace.TimedMetadata.createFromMetadata(
        id3.YMID,
        id3.YSEQ,
        id3.YTYP,
        id3.YDUR,
        startTime * 1000
      );
      this.session?.onTimedMetadata(meta);
    }
  }

  public override destroy(): void {
    this.session?.shutdown();
    this.session = undefined;
    super.destroy();
    this.destroyed = true;
  }
}
